import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Button from '@/shared/components/Button/Button'
import CheckBox from '@/shared/components/CheckBox/CheckBox'
import OverlayScrollbar from '@/shared/components/OverlayScrollbar/OverlayScrollbar'
import { useImageSlots } from '@/shared/store/useImageSlots'
import { deleteLocalAsset } from '../../assets/localAssets'
import { MAX_TEMPLATE_EXTRAS } from '../../config'
import { useDialogOpen } from '../../hooks/useDialogOpen'
import {
  ExtraBlock,
  SectionKey,
  SiteSections,
  SiteSettings
} from '../../schema/site'
import { effectiveVariant, templateOf } from '../../templates'
import {
  extraIdOfItem,
  listItems,
  reanchorExtras,
  sectionItemId
} from '../../utils/extrasOrder'
import { nextId } from '../../utils/ids'
import { gallerySlotIdsFrom, galleryWithout, photoSlotId } from '../../utils/photos'
import PhotoField from '../PhotoField/PhotoField'
import RoomPicker from '../RoomPicker/RoomPicker'
import formStyles from '../SiteSettingsForm/SiteSettingsForm.module.scss'
import ExtraCard from './ExtraCard'
import { sectionTitle } from './sectionTitle'
import VariantPicker from './VariantPicker'
import styles from './SectionsEditor.module.scss'

interface SectionsEditorProps {
  settings: SiteSettings
  language: string
  patch: (values: Partial<SiteSettings>) => void
  /** Whether the venue has dishes in menu management to show on the page. */
  hasMenu: boolean
}

/** The element id a section's card is found by, from the page and the preview. */
export const sectionCardId = (key: SectionKey) => `website-section-${key}`

/** The sections a template offers a choice of layouts for. */
const VARIANT_KEYS = ['hero', 'menu', 'about', 'visit', 'gallery'] as const

type VariantKey = (typeof VARIANT_KEYS)[number]

const hasVariants = (key: SectionKey): key is VariantKey =>
  (VARIANT_KEYS as readonly SectionKey[]).includes(key)

/**
 * A section's card: a fixed point in the list the blocks are dragged
 * through. It takes part in the sorting so the blocks can pass it and it
 * makes room for them, but it cannot itself be picked up: the order of the
 * sections is the template's.
 */
function SectionCard(props: {
  id: string
  cardId: string
  /** Dimmed when the section is off, so the list reads as what is on the page. */
  off: boolean
  children: React.ReactNode
}) {
  const { id, cardId, off, children } = props
  const { setNodeRef, transform, transition } = useSortable({
    id,
    disabled: true
  })
  return (
    <section
      ref={setNodeRef}
      id={cardId}
      className={`${styles.card} ${off ? styles.off : ''}`}
      // Computed geometry: where the card is while a block passes it.
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      {children}
    </section>
  )
}

/**
 * The template's sections, one card each, in the order the template puts
 * them on the page, with the owner's own blocks between them where they
 * fall on the page.
 *
 * What the owner may do to a section is decided here and it is little on
 * purpose: switch it off, pick one of the layouts the template offers for it,
 * and give it its photos. No section is added, moved or split; that is what
 * the free builder allowed and what made its pages nobody's. A block, on the
 * other hand, goes wherever it is dragged to, and lands after the section it
 * was dropped under.
 */
function SectionsEditor(props: SectionsEditorProps) {
  const { settings, language, patch, hasMenu } = props
  const { t } = useTranslation()
  const template = templateOf(settings)
  const { sections } = settings
  const { extras } = sections

  const clearSlot = useImageSlots(state => state.clear)
  const stripRef = useRef<HTMLDivElement | null>(null)
  // A bar is z-index 30 and this app's modals are 5 and 6, so one left drawn
  // over the crop dialog paints a line down the middle of it.
  const dialogOpen = useDialogOpen()

  /*
    A drag starts after the pointer has moved a little, so a press on the grip
    that is really a click does not pick the card up; the keyboard moves it
    with dnd-kit's own keys from the same grip.
  */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const patchSection = <K extends SectionKey>(
    key: K,
    values: Partial<SiteSections[K]>
  ) =>
    patch({
      sections: { ...sections, [key]: { ...sections[key], ...values } }
    })

  const setExtras = (next: ExtraBlock[]) =>
    patch({ sections: { ...sections, extras: next } })

  // At the bottom of the page, which is right above the button that made it;
  // from there it is dragged to where it belongs.
  const addExtra = () =>
    setExtras([
      ...extras,
      {
        id: nextId('extra'),
        after: template.order[template.order.length - 1],
        text: { [language]: '' },
        photoSide: 'left'
      }
    ])

  const updateExtra = (id: string, values: Partial<ExtraBlock>) =>
    setExtras(
      extras.map(extra => (extra.id === id ? { ...extra, ...values } : extra))
    )

  const removeExtra = (extra: ExtraBlock) => {
    // The photo goes with the block, the way the field's own delete does it;
    // this bypasses that button, so it has to do the same two things.
    if (extra.photo) deleteLocalAsset(extra.photo).catch(() => undefined)
    clearSlot(photoSlotId(`extra-${extra.id}`))
    setExtras(extras.filter(item => item.id !== extra.id))
  }

  /*
    Every section that has a card, which is the template's order without the
    map and the review button: both come from the venue's Google place, so
    their boxes live together in the group that speaks about it. A block
    anchored to either is listed last, where the page puts it (`listItems`).
  */
  const listedOrder = template.order.filter(
    key => key !== 'map' && key !== 'review'
  )

  const items = listItems(listedOrder, extras)

  /*
    Which block is in the air, so the pointer can carry a plate with its name
    on it while the card it came from stays put as an outline of the slot it
    will drop back into.
  */
  const [carried, setCarried] = useState<ExtraBlock | null>(null)

  const handleDragStart = ({ active }: DragStartEvent) => {
    const id = extraIdOfItem(String(active.id))
    setCarried(extras.find(extra => extra.id === id) ?? null)
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setCarried(null)
    if (!over || active.id === over.id) return
    const from = items.indexOf(String(active.id))
    const to = items.indexOf(String(over.id))
    if (from < 0 || to < 0) return
    setExtras(reanchorExtras(arrayMove(items, from, to), extras, listedOrder))
  }

  const setGalleryPhoto = (offset: number, url: string) => {
    const { photos } = sections.gallery
    if (url) {
      const next = [...photos]
      next[offset] = url
      patchSection('gallery', { photos: next })
      return
    }
    // The crops of every tile this shifts, so none of them is drawn against a
    // photo that is no longer there.
    gallerySlotIdsFrom(photos, offset).forEach(clearSlot)
    patchSection('gallery', { photos: galleryWithout(photos, offset) })
  }

  /*
    The layouts on offer, as pills on the card's own title line. A select for
    two or three names was a whole field tall with a floating label on top of
    it, and every section had one; read down the list, that was most of the
    height of the editor spent saying "Układ" seven times.
  */
  const variantPills = (key: VariantKey) => {
    const offered = template.variants[key]
    if (offered.length < 2) return null
    return (
      <VariantPicker
        label={t('WEBSITE.SECTIONS.VARIANT')}
        // The one the page is drawn in, which is the template's own where the
        // chosen one is not on offer; showing the stored one would disagree
        // with the preview beside it.
        value={effectiveVariant(template, key, sections[key].variant)}
        options={offered}
        translate={value => t(`WEBSITE.VARIANT.${key}.${value}`)}
        onChange={value =>
          patchSection(key, { variant: value } as Partial<
            SiteSections[typeof key]
          >)
        }
      />
    )
  }

  const hint = (key: string) => (
    <p className={`body-small ${formStyles.hint}`}>{t(key)}</p>
  )

  /*
    Under the title line, and only where a section has something more than its
    layout: a photo, the gallery strip, the rooms, the menu's two boxes. The
    sections that have none (the hours, the map) are one line each, and a note
    they do not need is a line of text between the cards that do.
  */
  const body = (key: SectionKey) => {
    switch (key) {
      case 'hero':
        return (
          <>
            {hint('WEBSITE.SECTIONS.HERO_HINT')}
            <PhotoField
              label={t('WEBSITE.PROPS.PHOTO')}
              slotId={photoSlotId('hero')}
              ratio={
                effectiveVariant(template, 'hero', sections.hero.variant) ===
                'split'
                  ? '4:3'
                  : '16:9'
              }
              value={sections.hero.photo ?? ''}
              // The schema refuses an empty reference; no photo is no field.
              onChange={url => patchSection('hero', { photo: url || undefined })}
            />
          </>
        )
      case 'menu':
        return hasMenu ? (
          <div className={styles.checks}>
            <CheckBox
              label={t('WEBSITE.SECTIONS.MENU_PRICES')}
              checked={sections.menu.showPrices}
              value={sections.menu.showPrices}
              onChange={value => patchSection('menu', { showPrices: value })}
            />
            <CheckBox
              label={t('WEBSITE.SECTIONS.MENU_PHOTOS')}
              checked={sections.menu.showPhotos}
              value={sections.menu.showPhotos}
              onChange={value => patchSection('menu', { showPhotos: value })}
            />
          </div>
        ) : (
          hint('WEBSITE.SECTIONS.MENU_NONE')
        )
      case 'about':
        return (
          <>
            {hint('WEBSITE.SETTINGS.PHOTO_ABOUT_HINT')}
            <PhotoField
              label={t('WEBSITE.PROPS.PHOTO')}
              slotId={photoSlotId('about')}
              ratio="4:3"
              value={sections.about.photo ?? ''}
              onChange={url =>
                patchSection('about', { photo: url || undefined })
              }
            />
          </>
        )
      case 'gallery':
        /*
          As many photos as somebody wants, so a row read sideways rather than
          a column that pushes the rest of the form down. The empty tile at the
          end is how another one is added, and a new empty tile takes its place
          the moment it is filled.
        */
        return (
          <>
            {hint('WEBSITE.SETTINGS.PHOTO_GALLERY_HINT')}
            <div className={styles.stripHost}>
              {!dialogOpen && <OverlayScrollbar targetRef={stripRef} axis="x" />}
              <div className={styles.photoStrip} ref={stripRef}>
                {[...sections.gallery.photos, ''].map((photo, offset) => (
                  <PhotoField
                    // Gallery photos have no identity beyond their position
                    // in the strip, which is the order they are shown in.
                    // eslint-disable-next-line react/no-array-index-key
                    key={offset}
                    slotId={photoSlotId(`gallery-${offset}`)}
                    label={t('WEBSITE.SETTINGS.PHOTO_NUMBERED', {
                      number: offset + 1
                    })}
                    ratio="4:3"
                    value={photo}
                    onChange={url => setGalleryPhoto(offset, url)}
                  />
                ))}
              </div>
            </div>
          </>
        )
      case 'reservations':
        // The picker itself says there is no room to pick, in an Alert, and
        // fetches the rooms it offers; the card asks it and shows the answer.
        return (
          <>
            {hint('WEBSITE.SETTINGS.ROOM_HINT')}
            <div className={styles.wide}>
              <RoomPicker
                label={t('WEBSITE.SETTINGS.ROOM_PICK')}
                value={settings.reservationRooms ?? []}
                onChange={next => patch({ reservationRooms: next })}
              />
            </div>
          </>
        )
      default:
        return null
    }
  }

  const sectionCard = (key: SectionKey) => {
    const enabled = key === 'hero' || sections[key].enabled
    const cardId = sectionCardId(key)
    const content = enabled ? body(key) : null
    return (
      <SectionCard
        key={key}
        id={sectionItemId(key)}
        cardId={cardId}
        off={!enabled}
      >
        <div className={styles.head}>
          {/*
            The box first, where a list of things that are on and off is read
            from: what it answers is the card it is on, so the title is its
            label. The one section that cannot be switched off has no box and
            no gutter in its place: its name starts at the card's own edge.
          */}
          {key !== 'hero' && (
            <CheckBox
              ariaLabel={t('WEBSITE.SECTIONS.SHOW')}
              checked={enabled}
              value={enabled}
              onChange={value =>
                patchSection(key, { enabled: value } as Partial<
                  SiteSections[typeof key]
                >)
              }
            />
          )}
          <p className={`title-bold-small ${styles.title}`}>
            {sectionTitle(key, language, t)}
          </p>
          {enabled && hasVariants(key) && variantPills(key)}
        </div>
        {content && <div className={styles.body}>{content}</div>}
      </SectionCard>
    )
  }

  const full = extras.length >= MAX_TEMPLATE_EXTRAS

  return (
    <section className={formStyles.section}>
      <p className={`title-bold-small ${formStyles.sectionTitle}`}>
        {t('WEBSITE.SECTIONS.TITLE')}
      </p>
      {hint('WEBSITE.SECTIONS.HINT')}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setCarried(null)}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          {/*
            One list, tight, so the page's order can be read down it. Each card
            carries its own border, which is what separates it from the next;
            the form's own 1.5rem between groups would have made seven groups
            out of one list.
          */}
          <div className={styles.list}>
            {items.map(item => {
              const extraId = extraIdOfItem(item)
              if (extraId === undefined) {
                return sectionCard(item.slice('section:'.length) as SectionKey)
              }
              const extra = extras.find(candidate => candidate.id === extraId)
              if (!extra) return null
              return (
                <ExtraCard
                  key={extra.id}
                  extra={extra}
                  number={extras.indexOf(extra) + 1}
                  language={language}
                  order={template.order}
                  onChange={values => updateExtra(extra.id, values)}
                  onRemove={() => removeExtra(extra)}
                />
              )
            })}
          </div>
        </SortableContext>
        {/*
          The plate under the pointer. `height: auto` because the overlay is
          sized from the card it came from, and that card is as tall as the
          fields in it; what is carried is the block's name, not its form.
        */}
        <DragOverlay style={{ height: 'auto' }}>
          {carried && (
            <div className={styles.dragTile}>
              <i
                className={`las la-grip-vertical icon-medium ${styles.dragGrip}`}
                aria-hidden="true"
              />
              <p className={`title-bold-small ${styles.dragTitle}`}>
                {t('WEBSITE.EXTRAS.BLOCK', {
                  number: extras.indexOf(carried) + 1
                })}
              </p>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* The blocks are in the list above; this is only how one more is made. */}
      <div className={styles.actions}>
        <Button
          label={t('WEBSITE.EXTRAS.ADD')}
          variant="outlined"
          color="primary"
          icon="las la-plus"
          disabled={full}
          onClick={addExtra}
        />
        {hint(full ? 'WEBSITE.EXTRAS.MAX_HINT' : 'WEBSITE.EXTRAS.HINT')}
      </div>
    </section>
  )
}

export default SectionsEditor
